import { useCallback, useEffect, useRef, useState } from 'react';

import { debounce } from 'lodash';
import { get } from 'helpers/api';
import { RECORD_CHANGED_EVENT } from 'helpers/record_changed';
import { getDefaults, hasFilters, readDeepLinkedFilters, serializeFilters } from './filters/helpers';

import type { RecordChange } from 'helpers/record_changed';
import type { Activity, PaginationData, StageOption, TimelineStageEvent } from './types';
import type { ApiFilters, Filters } from './filters/types';

export type Paths = {
  index?: string;
}

type RequestFlags = Pick<ApiFilters, 'needsReplyCount' | 'showPinned' | 'stageEvents'>;

// For reuse in the event listener setup
export const refreshEventName = 'refreshActivities';

// `fresh` is honoured only for an unfiltered timeline under a parent - anything else is answered
// from Elasticsearch (Apex::ActivitiesController#fresh_from_database?).
const readsSearchIndex = (path: string, filters: Filters) => {
  const params = new URLSearchParams(path.split('?')[1]);

  return hasFilters(filters) || !params.has('parent_id') || !params.has('parent_type');
};
const defaultFilters = getDefaults();

const byServerOrder = (a: Activity, b: Activity) => {
  const dateDiff = new Date(b.date).getTime() - new Date(a.date).getTime();
  return dateDiff !== 0 ? dateDiff : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
};

// `fresh` is opt-in: pass it only from a call site that just created an activity, so the refetch
// reads it from MySQL instead of an Elasticsearch index that may not have caught up yet.
export function refreshActivities({ fresh = false }: { fresh?: boolean } = {}) {
  window.dispatchEvent(new CustomEvent(refreshEventName, { detail: { fresh } }));
}

export function useActivitiesState(
  {
    activities: externalActivities, paginate, paths, showPinned, seedFiltersFromUrl, withNeedsReplyCount,
    withStageEvents, onPageDataChange, onChange
  }: {
    activities?: Activity[],
    paths?: Paths,
    paginate?: boolean,
    showPinned?: boolean,
    seedFiltersFromUrl?: boolean,
    withNeedsReplyCount?: boolean,
    withStageEvents?: boolean,
    onPageDataChange?: (pageData: PaginationData) => void,
    onChange?: (_: Activity[]) => void
  } = {}
) {
  const [activities, setActivities] = useState(externalActivities || []);
  const [pinnedActivities, setPinnedActivities] = useState<Activity[]>([]);
  const [stageEvents, setStageEvents] = useState<TimelineStageEvent[]>([]);
  const [stageOptions, setStageOptions] = useState<StageOption[]>([]);
  const [loaded, setLoaded] = useState(!!externalActivities || !paths?.index);
  const [loading, setLoading] = useState(!loaded);
  const [error, setError] = useState(false);
  const [filters, _setFilters] = useState<Filters>(
    () => (seedFiltersFromUrl && readDeepLinkedFilters()) || defaultFilters
  );
  const [pageData, setPageData] = useState<PaginationData | null>(null);
  const [needsReplyCount, setNeedsReplyCount] = useState(0);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Monotonic id so a slow, stale request can't overwrite the results (or error) of a newer one.
  const requestIdRef = useRef(0);

  function handleResponse(response) {
    setLoading(false);
    setError(false);
    setActivities(response.activities);
    setPinnedActivities(response.pinnedActivities || []);
    setStageEvents(response.stageEvents || []);
    setStageOptions(response.stageOptions || []);

    if (response.needsReplyCount != null) {
      setNeedsReplyCount(response.needsReplyCount);
    }

    if (response.meta) {
      setPageData(response.meta);

      if (onPageDataChange) {
        onPageDataChange(response.meta as PaginationData);
      }
    }
  }

  function handleError() {
    setLoading(false);
    setError(true);
  }

  async function fetchInto(path: string, options: object, { silent = false } = {}) {
    const requestId = ++requestIdRef.current;

    try {
      const response = Object.keys(options).length ? await get(path, options) : await get(path);
      if (requestId === requestIdRef.current) handleResponse(response);
    } catch {
      if (requestId === requestIdRef.current && !silent) handleError();
    }
  }

  const requestFlags = () => {
    const flags: RequestFlags = {};

    if (showPinned) flags.showPinned = true;
    if (withNeedsReplyCount) flags.needsReplyCount = true;
    if (withStageEvents) flags.stageEvents = true;

    return flags;
  };

  const loadActivities = async (filters: Filters, { showSpinner = false } = {}) => {
    if (!paths?.index) return;

    if (showSpinner) {
      setLoading(true);
      setError(false);
    }

    if (hasFilters(filters)) {
      await searchActivities(paths.index, serializeFilters(filters));
    } else {
      // Without a page the index returns the whole timeline, so a paginated list must always send one
      const params = paginate ? { ...requestFlags(), page: filters.page } : requestFlags();
      await fetchInto(paths.index, Object.keys(params).length ? { params } : {});
    }

    setLoaded(true);
  };

  const reload = () => loadActivities(filtersRef.current, { showSpinner: true });

  useEffect(() => {
    if (paths?.index && !loaded) {
      loadActivities(filters, { showSpinner: true });
    }

    let servedFromDatabase = false;

    // Always through searchActivities: an unfiltered loadActivities hits the plain index, which
    // would bounce the user off whatever page they are on. `silent` keeps a failed background refetch
    // from replacing the list the user is reading with an error screen.
    const refetchCreated = () => {
      if (!paths?.index) return;

      servedFromDatabase = !readsSearchIndex(paths.index, filtersRef.current);
      searchActivities(paths.index, serializeFilters(filtersRef.current), { fresh: true, silent: true });
    };

    const onRecordChanged = (event: Event) => {
      const change = (event as CustomEvent<RecordChange>).detail;
      if (change?.kind !== 'activity_created') return;
      if (change.retry && servedFromDatabase && !hasFilters(filtersRef.current)) return;

      refetchCreated();
    };

    // A refresh must not silently drop the filters the user is looking at
    const onRefresh = (event: Event) => {
      const { fresh } = (event as CustomEvent<{ fresh?: boolean }>).detail || {};

      if (fresh) {
        refetchCreated();
      } else {
        loadActivities(filtersRef.current);
      }
    };

    window.addEventListener(refreshEventName, onRefresh);
    window.addEventListener(RECORD_CHANGED_EVENT, onRecordChanged);

    return () => {
      window.removeEventListener(refreshEventName, onRefresh);
      window.removeEventListener(RECORD_CHANGED_EVENT, onRecordChanged);
    };
  }, [paths?.index]);

  useEffect(() => {
    // Currently, the activities component handles editing and deletion, but not adding an activity.
    // On deals, activity creation is handled externally, and the activities are also stored in the
    // deal's state. While we need to be able to ingest those added activities here, we need to
    // avoid infinite rendering loops triggered by this component exposing a change to the deal,
    // which updates it's store, which feeds that new object back in here, etc. Since the only thing
    // that can happen externally is creation, just checking length for a change is sufficient.
    // NOTE: This is weird but should be able to be cleaned up when activity creating moves out of
    //       deal code and into this standalone activity module
    if (externalActivities && externalActivities.length !== activities.length) {
      setActivities(externalActivities);
    }
  }, [externalActivities]);

  function changeActivities(changeFunction) {
    setActivities((activities) => {
      const newActivities = changeFunction(activities);

      if (onChange) {
        onChange(newActivities);
      }

      return newActivities;
    });
  }

  const addActivity = useCallback((savedActivity) => {
    changeActivities(activities => [savedActivity, ...activities]);
  }, []);

  const replaceActivity = useCallback((savedActivity) => {
    const replace = (list: Activity[]) => {
      return list.map(activity => activity.id === savedActivity.id ? savedActivity : activity);
    };

    changeActivities(replace);
    setPinnedActivities(replace);
  }, []);

  const removeActivity = useCallback((removedActivity) => {
    const without = (list: Activity[]) => list.filter(activity => activity.id !== removedActivity.id);

    changeActivities(without);
    setPinnedActivities(without);
  }, []);

  const adjustMainCount = (delta: number) => {
    setPageData((current) => {
      if (!current) return current;

      const totalCount = Math.max(0, current.totalCount + delta);

      return {
        ...current,
        itemCount: Math.max(0, current.itemCount + delta),
        totalCount,
        totalPages: Math.max(1, Math.ceil(totalCount / current.pageSize)),
      };
    });
  };

  const changePinned = useCallback((savedActivity: Activity, pinned: boolean) => {
    const withoutSaved = (list: Activity[]) => list.filter(a => a.id !== savedActivity.id);
    const insertSorted = (list: Activity[]) => [savedActivity, ...withoutSaved(list)].sort(byServerOrder);

    if (pinned) {
      changeActivities(withoutSaved);
      setPinnedActivities(insertSorted);
    } else {
      setPinnedActivities(withoutSaved);
      changeActivities(insertSorted);
    }

    adjustMainCount(pinned ? -1 : 1);
  }, []);

  const setFilters = useCallback(async (filters: Filters, debounce = false) => {
    _setFilters(filters);

    if (paths?.index) {
      setLoading(true);
      setError(false);
      const performSearch = debounce ? debouncedSearch : searchActivities;
      performSearch(paths.index, serializeFilters(filters));
    }
  }, [paths?.index]);

  const changeFilters = (changes: Partial<Filters>, debounce = false) => {
    const changedFilters = { ...filters, page: 1, ...changes };
    setFilters(changedFilters, debounce);
  };

  const debouncedSearch = debounce(searchActivities, 500);
  async function searchActivities(path: string, filters: ApiFilters, { fresh = false, silent = false } = {}) {
    if (!paginate) {
      delete filters.page;
    }

    Object.assign(filters, requestFlags());
    if (fresh) filters.fresh = true;

    await fetchInto(path, { params: filters }, { silent });
  }

  return {
    activities,
    pinnedActivities,
    stageEvents,
    stageOptions,
    loading,
    error,
    reload,
    needsReplyCount,
    pageData,
    addActivity,
    replaceActivity,
    removeActivity,
    changePinned,
    filters,
    changeFilters,
  };
}
